import { useEffect, useMemo } from 'react';
import { usePreviousValue } from '@moda/portal-stanchions';
import { pick, symmetricDifference } from 'ramda';
import { SortOption } from '../constants';
import { AvailabilityInput, VariantCellFragment } from '../generated/types';
import { tc } from '../lib/trackingContext';
import { usePLPParams } from './usePLPParams';

type Type = {
  variants: VariantCellFragment[];
  availableSorts: SortOption[];
  loading: boolean;
};

const FILTER_FIELDS = ['availability', 'attributes', 'designers', 'colors', 'sizes'] as const;

export const useFilterEngagementTracking = ({ variants, loading, availableSorts }: Type) => {
  const { params, currentSort } = usePLPParams(availableSorts);
  const previousParams = usePreviousValue(params);
  const previousSort = usePreviousValue(currentSort);

  const selectedParams = useMemo(
    () => pick([...FILTER_FIELDS, 'pricemin', 'pricemax'], params),
    [params]
  );

  const changedFilter =
    previousParams &&
    symmetricDifference(Object.entries(params), Object.entries(previousParams))[0];

  const filteringTriggered = Boolean(
    changedFilter && FILTER_FIELDS.includes(changedFilter[0] as (typeof FILTER_FIELDS)[number])
  );
  const sortingTriggered = Boolean(previousSort && previousSort?.value !== currentSort.value);

  const sanitizedFilters = useMemo(() => {
    const filters: {
      type: string;
      value: string | number | boolean | string[] | AvailabilityInput[];
    }[] = [];

    Object.entries(selectedParams).map(([key, value]) => {
      if ((!Array.isArray(value) && value) || (Array.isArray(value) && value.length > 0)) {
        filters.push({ type: key, value: value });
      }
    });

    return filters;
  }, [selectedParams]);

  useEffect(() => {
    if ((filteringTriggered || sortingTriggered) && !loading) {
      tc.track('Product List Filtered', {
        addToScopeOnce: {
          products: variants,
          filters: sanitizedFilters,
          sorts: [{ type: currentSort.name, value: currentSort.value }],
          label: JSON.stringify(sanitizedFilters)
        }
      });
    }
  }, [variants, loading, currentSort, filteringTriggered, sortingTriggered, sanitizedFilters]);
};
